// HoverModalButton.tsx
import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { styled } from "leva/plugin";
import Modal from "./Modal";
import usePositionRelativeToParent from "../lib/usePositionRelativeToParent";
import SliderContext from "../context/SliderContext";

const Button = styled("button", {
	backgroundColor: "$elevation2",
	color: "$highlight2",
	borderRadius: "4px",
	padding: "0 10px",
	height: "100%",
	border: "none",
	cursor: "pointer",
	transition: "background-color 0.2s",
	outline: "none", // Add this line to remove the focus ring

	"&.hovered": {
		backgroundColor: "$elevation3",
		color: "$highlight3",
	},
	"&.disabled": {
		color: "$highlight1",
		pointerEvents: "none",
	},
});

const ButtonWrapper = styled("div", {
	// position: "relative",
	height: "100%",
});

const GapCover = styled("div", {
	position: "absolute",
	bottom: "calc(100%-10px)",
	height: "16px",
	zIndex: 9,
	top: "-10px",
});

const HoverModalButton = ({
	children,
	label,
	onClick,
	staticOn = false,
	disabled = false,
	modalAlign = "left",
}: {
	children?: React.ReactNode[];
	label: string;
	onClick: () => void;
	staticOn?: boolean;
	disabled?: boolean;
	modalAlign?: "left" | "right" | "center" | "center_right" | "center_left";
}) => {
	const [isModalVisible, setIsModalVisible] = useState(staticOn);
	const [ref, position] = usePositionRelativeToParent();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [buttonWidth, setButtonWidth] = useState(0);
	const [isDragging, setIsDragging] = useState(false);
	const leaveTimeout = useRef<NodeJS.Timeout | null>(null); // add a ref to store the timeout

	useLayoutEffect(() => {
		const updateButtonWidth = () => {
			if (buttonRef.current) {
				setButtonWidth(buttonRef.current.getBoundingClientRect().width);
			}
		};
		updateButtonWidth();
		window.addEventListener("resize", updateButtonWidth);
		return () => {
			window.removeEventListener("resize", updateButtonWidth);
		};
	}, []);

	const handleMouseEnter = () => {
		if (!staticOn && !disabled) {
			if (leaveTimeout.current) clearTimeout(leaveTimeout.current); // if a timeout is set, clear it
			setIsModalVisible(true);
		}
	};

	const handleMouseLeave = () => {
		if (!staticOn && !disabled) {
			leaveTimeout.current = setTimeout(() => {
				// set a timeout before closing the menu
				setIsModalVisible(false);
			}, 65); // adjust the delay as needed
		}
	};

	useEffect(() => {
		return () => {
			if (leaveTimeout.current) clearTimeout(leaveTimeout.current);
		};
	}, []);

	return (
		<ButtonWrapper
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
		>
			<Button
				ref={buttonRef}
				onClick={onClick}
				className={`${isModalVisible && "hovered"} ${disabled && "disabled"}`}
			>
				{label}
			</Button>
			<GapCover
				style={{
					width: buttonWidth,
					display: !isModalVisible ? "none" : "block",
				}}
			/>
			{children && (
				<SliderContext.Provider value={{ isDragging, setIsDragging }}>
					<Modal
						isDragging={isDragging}
						modalAlign={modalAlign}
						ref={ref}
						position={position}
						children={children}
						isVisible={isModalVisible}
						buttonWidth={buttonWidth}
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					/>
				</SliderContext.Provider>
			)}
		</ButtonWrapper>
	);
};

export default HoverModalButton;
